import React, { Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import GeneralLoading from "../components/GeneralLoading";
import ProtectedRoute from "./ProtectedRoute";
import { PATHS } from "../utils/paths";
import Layout from "../components/Layout";

const Register = React.lazy(() => import("../pages/auth/Register"));
const Login = React.lazy(() => import("../pages/auth/Login"));
const Verification = React.lazy(() => import("../pages/auth/Verification"));
const ForgotPassword = React.lazy(() => import("../pages/auth/ForgotPassword"));
const NewPassword = React.lazy(() => import("../pages/auth/NewPassword"));
const CompleteProfile = React.lazy(
  () => import("../pages/auth/CompleteProfile"),
);
const Dashboard = React.lazy(() => import("../pages/dashboard"));
const MyLessons = React.lazy(() => import("../pages/myLessons"));
const Lessons = React.lazy(() => import("../pages/lessons"));
const Levels = React.lazy(() => import("../pages/levels"));
const Exercises = React.lazy(() => import("../pages/levels/exercises"));
const Model3DPage = React.lazy(() => import("../pages/levels/3DModel"));
const EditorPage = React.lazy(() => import("../pages/editor"));
const NotFound = React.lazy(() => import("../pages/NotFound")); // UX-11
const ConversationsPage = React.lazy(() => import("../pages/chat"));
const LeaderboardPage = React.lazy(() => import("../pages/leaderboard"));
const SettingsPage = React.lazy(() => import("../pages/settings"));
const FinalExamPage = React.lazy(() => import("../pages/finalExam"));
const FeedPage = React.lazy(() => import("../pages/feed"));
const ProfilePage = React.lazy(() => import("../pages/profile"));
const Landing = React.lazy(() => import("../pages/landing"));

const AppRoute = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path={PATHS.Login}
        element={
          <ProtectedRoute>
            <Suspense fallback={<GeneralLoading />}>
              <Login />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={PATHS.Register}
        element={
          <ProtectedRoute>
            <Suspense fallback={<GeneralLoading />}>
              <Register />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={PATHS.Verification}
        element={
          <ProtectedRoute>
            <Suspense fallback={<GeneralLoading />}>
              <Verification />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path={PATHS.ForgotPassword}
        element={
          <ProtectedRoute>
            <Suspense fallback={<GeneralLoading />}>
              <ForgotPassword />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path={PATHS.NewPassword}
        element={
          <ProtectedRoute>
            <Suspense fallback={<GeneralLoading />}>
              <NewPassword />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path={PATHS.CompleteProfile}
        element={
          <ProtectedRoute>
            <Suspense fallback={<GeneralLoading />}>
              <CompleteProfile />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/"
        element={
          <Suspense fallback={<GeneralLoading />}>
            <Landing />
          </Suspense>
        }
      />
      <Route
        path={PATHS.PrivacyPolicy}
        element={
          <Suspense fallback={<GeneralLoading />}>
            <Landing />
          </Suspense>
        }
      />
      <Route
        path={PATHS.TermsOfUse}
        element={
          <Suspense fallback={<GeneralLoading />}>
            <Landing />
          </Suspense>
        }
      />

      {/* Layout + protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route
          path={PATHS.Learn}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <Dashboard />
            </Suspense>
          }
        />

        <Route
          path={PATHS.Leaderboard}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <LeaderboardPage />
            </Suspense>
          }
        />

        <Route
          path={PATHS.MyLessons}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <MyLessons />
            </Suspense>
          }
        />

        <Route
          path={PATHS.Lessons}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <Lessons />
            </Suspense>
          }
        />

        <Route
          path={PATHS.Conversations}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <ConversationsPage />
            </Suspense>
          }
        />

        <Route
          path={PATHS.ConversationDetail}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <ConversationsPage />
            </Suspense>
          }
        />

        <Route
          path={PATHS.ConversationsCreateGroup}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <ConversationsPage />
            </Suspense>
          }
        />

        <Route
          path={PATHS.Settings}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <SettingsPage />
            </Suspense>
          }
        />

        <Route
          path={PATHS.Feed}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <FeedPage />
            </Suspense>
          }
        />

        <Route
          path={PATHS.Profile}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <ProfilePage />
            </Suspense>
          }
        />

        <Route
          path={`${PATHS.Profile}/:username`}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <ProfilePage />
            </Suspense>
          }
        />

        <Route
          path={PATHS.FinalExam}
          element={
            <Suspense fallback={<GeneralLoading />}>
              <FinalExamPage />
            </Suspense>
          }
        />
      </Route>

      {/* Levels */}
      <Route
        path={PATHS.Levels}
        element={
          <ProtectedRoute>
            <Suspense fallback={<GeneralLoading />}>
              <Levels />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={PATHS.Exercises}
        element={
          <ProtectedRoute>
            <Suspense fallback={<GeneralLoading />}>
              <Exercises />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={PATHS.Model3D}
        element={
          <ProtectedRoute>
            <Suspense fallback={<GeneralLoading />}>
              <Model3DPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path={PATHS.Editor}
        element={
          <ProtectedRoute>
            <Suspense fallback={<GeneralLoading />}>
              <EditorPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Catch-all 404: Suspense-wrapped like every other lazy route (UX-11).
          Without the boundary the lazy NotFound suspended with no fallback and
          the root ErrorBoundary showed "Something went wrong" instead. */}
      <Route
        path="*"
        element={
          <Suspense fallback={<GeneralLoading />}>
            <NotFound />
          </Suspense>
        }
      />
    </Routes>
  );
};

export default AppRoute;
